import {
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Menu,
  MenuItem,
} from '@mui/material'
import { useAtom } from 'jotai'
import type { FC, ReactNode } from 'react'
import { useMemo, useState } from 'react'
import type { PressEvent } from 'react-aria-components'
import {
  Link as AriaLink,
  Disclosure,
  DisclosurePanel,
} from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'
import { safeParse } from 'valibot'
import FluentAdd24Filled from '~icons/fluent/add-24-filled'
import IcBaselineDiscord from '~icons/ic/baseline-discord'
import IcBaselineExpandMore from '~icons/ic/baseline-expand-more'
import MaterialSymbolsInstallMobileRounded from '~icons/material-symbols/install-mobile-rounded'
import MaterialSymbolsNavigateBefore from '~icons/material-symbols/navigate-before'
import SolarCupStarLinear from '~icons/solar/cup-star-linear'
import NewLabel from 'src/components/NewLabel'
import Text from 'src/components/ui/Text'
import { useContestV2Experiment } from 'src/experiments/experiments/2604/contestV2'
import { AIToolsConfigSchema } from 'src/features/AIToolsLanding/common'
import { useSocialMedia } from 'src/hooks/socialMedia'
import { useDynamicConstants } from 'src/services/constants'
import { useMe } from 'src/services/helpers/user'
import { usePublicDynamicConfig } from 'src/services/restful/dynamicConfig'
import { desktopNavigationExpandAtom } from 'src/stores/navigation'
import { cx } from 'src/utils/cx'
import { useExperimentParam } from 'src/utils/experimentNew'
import { useAgentMioUrl } from 'src/utils/hooks/useAgentMioUrl'
import { useIsMobile } from 'src/utils/hooks/useBreakpoint'
import { useLocation } from 'src/utils/router/route'
import { startEventGroup, track } from 'src/utils/tracker'
import { isExternalUrl } from 'src/utils/url'
import { openSignModal } from '../Account/utils'
import { openAppDownloadDialog } from '../AppDownload'
import { Mio2Logo } from '../Logo'
import { OutboundLink } from '../OutboundLink'
import type { UIButtonProps } from '../ui/Button'
import { UIButton } from '../ui/Button'
import { UILink } from '../ui/Link'
import { UITooltipTriggerWrapper } from '../ui/Tooltip'
import {
  NavGridIcon,
  NavHomeIcon,
  NavMagicWandIcon,
  NavMineIcon,
  NavPaletteIcon,
} from './NavIconsExperiment'

type NavItemData = {
  name: string
  title: string
  icon?: FC<{
    className?: string
  }>
  link?: string
  target?: React.HTMLAttributeAnchorTarget
  className?: string
  onClick?: () => void
  children?: NavItemData[]
  tracker?: {
    event: string
    attrs?: any
  }
  badge?: string | ReactNode
  experiment?: string
  activeIcon?: FC<{
    className?: string
  }>
}

function AppNavigationDesktop() {
  const [showExpandedAppNav, setShowExpandedAppNav] = useAtom(
    desktopNavigationExpandAtom,
  )

  const toggleDesktopNavigationExpanded = () => {
    setShowExpandedAppNav(v => !v)
  }

  const agentMioUrl = useAgentMioUrl({ utmContent: 'menu_bar' })
  const { isV2: contestV2Gate } = useContestV2Experiment()

  const navItemType = showExpandedAppNav ? 'expanded' : 'shrinked'
  return (
    <aside className="flex flex-col justify-start w-[--app-nav-width] shadow-[1px_0px_#0002] dark:shadow-[inset_-1px_0px_#fff2] relative transition-[width]">
      <div className="sticky top-[calc(var(--app-header-height)+1em)] w-[calc(100%+0.75em)] h-0 overflow-visible flex justify-end z-100 transition-all">
        <button
          className="rounded-full bg-background hover:bg-background-secondary drop-shadow-lg transition-transform duration-150 size-6 dark:ring-2 dark:ring-neutral-500"
          onClick={toggleDesktopNavigationExpanded}
        >
          <MaterialSymbolsNavigateBefore
            className={cx(
              'size-6',
              showExpandedAppNav ? 'rotate-0' : 'rotate-180',
            )}
          />
        </button>
      </div>
      <nav className="sticky top-[--app-header-height] flex flex-col py-1">
        <NavItem
          type={navItemType}
          item={{
            name: 'home',
            title: 'common:home.label',
            icon: NavHomeIcon,
            link: '/',
          }}
        />
        <NavItem
          type={navItemType}
          item={{
            name: 'generation',
            title: 'common:tab.generation',
            icon: NavPaletteIcon,
            link: '/generator/image',
            onClick: () => {
              startEventGroup('generate_source')
            },
          }}
        />
        <NavItem
          type={navItemType}
          item={{
            name: 'market',
            title: 'common:tab.models',
            icon: NavMagicWandIcon,
            link: '/market',
          }}
        />
        {contestV2Gate && (
          <NavItem
            type={navItemType}
            item={{
              name: 'contest',
              title: 'common:tab.contest',
              icon: SolarCupStarLinear,
              link: '/contest',
            }}
          />
        )}

        <NavItem
          type={navItemType}
          item={{
            name: 'generation-mio',
            title: 'common:tab.sites.generation-mio2',
            icon: Mio2Logo,
            link: agentMioUrl,
            tracker: {
              event: 'mio_clicked',
            },
          }}
        />
        <MoreToolsNavItem type={navItemType} />
      </nav>
      <span className="mt-auto" />
      <div className="sticky bottom-0 bg-background">
        <NavItem
          type={navItemType}
          item={{
            name: 'mobile',
            title: 'common:tab.mobile.label',
            icon: MaterialSymbolsInstallMobileRounded,
            onClick: () => {
              void openAppDownloadDialog({ trackingArgs: { source: 'user' } })
            },
          }}
        />
        <NavItemIconGroup expanded={showExpandedAppNav} />
      </div>
    </aside>
  )
}

function MoreToolsNavItem(props: { type: 'expanded' | 'shrinked' }) {
  const { type } = props
  const { data, isLoading } = usePublicDynamicConfig('toolsLanding')

  const subtitleElement = useMemo(() => {
    if (isLoading) return null
    const parsed = safeParse(AIToolsConfigSchema, data)
    if (!parsed.success) return null
    const newFeatureLabel = parsed.output.landing.newFeature
    if (!newFeatureLabel) return null
    if (type !== 'expanded') return null

    return (
      <p className="truncate text-xs text-neutral-500">
        <Text i18nKey={newFeatureLabel} />
      </p>
    )
  }, [data, isLoading, type])

  return (
    <NavItem
      type={type}
      item={{
        name: 'tool',
        title: 'common:tab.sites.more-tools',
        badge: 'new',
        icon: NavGridIcon,
        link: '/generator/preset',
      }}
      subtitleSlot={subtitleElement}
    />
  )
}

function NavItemIconGroup({ expanded }: { expanded: boolean }) {
  const { data } = useDynamicConstants()
  const socialMedias = useSocialMedia()

  if (expanded) {
    return (
      <ul className="flex px-3 my-2 justify-between">
        {socialMedias.map((m, idx) => (
          <UITooltipTriggerWrapper
            key={idx}
            delay={250}
            className="px-2 py-1"
            trigger={
              <AriaLink href={m.link} target="_blank" rel="nofollow noopener">
                <li className="bg-transparent hover:bg-neutral-200 dark:hover:bg-neutral-600 hover:drop-shadow-md p-2 rounded-md transition-all cursor-pointer group">
                  <m.icon className="size-5 opacity-80 group-hover:opacity-100" />
                </li>
              </AriaLink>
            }
          >
            {m.name}
          </UITooltipTriggerWrapper>
        ))}
      </ul>
    )
  } else {
    return (
      <NavItem
        type="shrinked"
        item={{
          name: 'discord',
          title: 'common:join-discord',
          icon: IcBaselineDiscord,
          link: data?.externalUrls?.discordJoin,
        }}
      />
    )
  }
}

const navItemStyles = tv({
  slots: {
    root: '',
    icon: 'flex-none opacity-70 transition-all',
    label: '',
    labelText: 'md:text-sm text-xs leading-none',
  },
  variants: {
    type: {
      shrinked: '',
      expanded: '',
      'list-item': '',
    },
  },
  compoundSlots: [
    {
      slots: ['root'],
      type: 'list-item',
      className: 'md:h-8',
    },
    {
      slots: ['root'],
      type: 'expanded',
      className: 'md:h-12',
    },
    {
      slots: ['root'],
      type: 'shrinked',
      className: 'md:h-20',
    },
    {
      slots: ['root'],
      type: ['expanded', 'list-item'],
      className: 'flex-row gap-2 ml-0 justify-start px-3',
    },
    {
      slots: ['root'],
      type: 'shrinked',
      className: 'flex-col grow gap-1 justify-center h-16 text-primary',
    },
    {
      slots: ['icon'],
      type: 'list-item',
      className: 'size-5',
    },
    {
      slots: ['icon'],
      type: 'expanded',
      className: 'size-6',
    },
    {
      slots: ['icon'],
      type: 'shrinked',
      className: 'size-8',
    },
    {
      slots: ['label'],
      type: ['expanded', 'list-item'],
      className: 'flex-grow',
    },
    {
      slots: ['label'],
      type: 'shrinked',
      className: 'whitespace-normal max-w-full',
    },
    {
      slots: ['labelText'],
      type: ['expanded', 'list-item'],
      className: 'text-left',
    },
    {
      slots: ['labelText'],
      type: 'shrinked',
      className: 'text-center overflow-hidden break-words line-clamp-2',
    },
    {
      slots: ['labelText'],
      type: ['shrinked', 'expanded'],
      className: 'font-normal',
    },
    {
      slots: ['labelText'],
      type: 'list-item',
      className: 'font-normal',
    },
  ],
})

interface NavItemProps
  extends StyleProps, Pick<VariantProps<typeof navItemStyles>, 'type'> {
  item: NavItemData
  hidden?: boolean
  subtitleSlot?: ReactNode
}

function NavItem(props: NavItemProps) {
  const { item, type, className, subtitleSlot, hidden = false, style } = props
  const location = useLocation()
  const active = item.link
    ? item.link === '/'
      ? location.pathname === item.link
      : location.pathname.startsWith(item.link)
    : false

  const expanded = type !== 'shrinked'
  const hasChildren = item.children && item.children.length > 0
  const [anchorEl, setAnchorEl] = useState<HTMLElement>()

  const enabled = useExperimentParam<boolean>(
    item.experiment ?? '',
    !item.experiment,
  )
  const [showChildren, setShowChildren] = useState(false)

  if (!enabled) return null

  const styles = navItemStyles({ type })

  const buttonProps = {
    variant: 'ghost',
    slot: 'trigger',
    className: cx(
      'w-[calc(100%-0.5em)] rounded-none items-center flex transition-all',
      'md:rounded-md md:mx-1 md:my-0.5 md:grow-0 relative ring-0 outline-0',
      'hover:bg-purple-600 hover:bg-opacity-10 no-underline',
      active ? 'bg-purple-600 bg-opacity-10 dark:bg-opacity-30' : '',
      styles.root(),
      hidden && 'hidden',
      className,
      // Prevent badge being shrinked
      item.badge && '[&>:last-child]:shrink-0',
    ),
    style,
    onPress: (ev: PressEvent) => {
      track('nav_btn_click', { cate: item.name })
      item.onClick?.()
      if (hasChildren && !expanded) {
        setAnchorEl(ev.target as HTMLElement)
      }
    },
    icon:
      active && item.activeIcon ? (
        <item.activeIcon className={styles.icon()} />
      ) : (
        item.icon && <item.icon className={styles.icon()} />
      ),
    labelClassName: styles.label(),
    endIcon:
      typeof item.badge === 'string' ? (
        <NavItemBadge type={item.badge} />
      ) : item.badge ? (
        item.badge
      ) : expanded && hasChildren ? (
        <IcBaselineExpandMore
          className={cx('transition-all', showChildren && 'rotate-180')}
        />
      ) : undefined,
  } satisfies UIButtonProps

  const button = (
    <UIButton {...buttonProps}>
      <div className={styles.labelText()}>
        <Text i18nKey={item.title} />
      </div>
    </UIButton>
  )

  return (
    <>
      {!hasChildren &&
        (!item.link ? (
          button
        ) : (
          <UILink
            href={item.link!}
            target={
              item.target ?? (isExternalUrl(item.link!) ? '_blank' : undefined)
            }
            rel={isExternalUrl(item.link!) ? 'noopener noreferrer' : undefined}
            {...buttonProps}
            onClick={() => {
              track('nav_btn_click', { cate: item.name })
            }}
          >
            <div className={styles.labelText()}>
              <Text i18nKey={item.title} />
              {subtitleSlot}
            </div>
          </UILink>
        ))}
      {hasChildren && !expanded && (
        <NavItemMenu
          button={button}
          items={item.children!}
          anchorEl={anchorEl}
          hide={() => setAnchorEl(undefined)}
        />
      )}
      {hasChildren && expanded && (
        <NavItemCollapseMenu
          button={button}
          items={item.children!}
          isExpanded={showChildren}
          onExpandedChange={setShowChildren}
        />
      )}
    </>
  )
}

function NavItemBadge(props: { type: string }) {
  switch (props.type) {
    case 'new':
      return <NewLabel className="absolute top-0 right-0" />
    default:
      return null
  }
}

type NavItemMenuProps = {
  button: ReactNode
  items: NavItemData[]
  anchorEl: HTMLElement | undefined
  hide: () => void
}

function NavItemMenu({ button, items, anchorEl, hide }: NavItemMenuProps) {
  const isMobile = useIsMobile()

  const open = anchorEl != null

  const { t } = useTranslation(['common', 'action', 'generation'])

  function onClick(item: NavItemData) {
    if (item.tracker) {
      track(item.tracker.event, item.tracker.attrs)
    }

    item.onClick?.()

    if (!item.link) return

    // If it is an external link
    if (isExternalUrl(item.link)) {
      window.open(item.link, '_blank', 'noopener,noreferrer')
    }
  }

  const menu = isMobile ? (
    <Drawer anchor="bottom" open={open} onClose={hide} elevation={4}>
      <List className="bg-background-secondary">
        {items.map(item => (
          <NavItemMenuMobileListItem
            key={item.title}
            item={item}
            onClick={() => onClick(item)}
            className="h-full text-base font-medium"
          />
        ))}
        <ListItem>
          <ListItemButton onClick={hide}>{t('action:cancel')}</ListItemButton>
        </ListItem>
      </List>
    </Drawer>
  ) : (
    <Menu
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: isMobile ? 'left' : 'right',
      }}
      transformOrigin={{
        vertical: isMobile ? 'bottom' : 'top',
        horizontal: 'left',
      }}
      onClose={hide}
      disableScrollLock
      elevation={2}
      slotProps={{
        paper: {
          className: 'bg-background-secondary',
        },
      }}
    >
      {items.map(item => (
        <NavItemMenuDesktopMenuItem
          key={item.title}
          item={item}
          className="h-10"
          onClick={() => {
            onClick(item)
            hide()
          }}
        />
      ))}
    </Menu>
  )

  return (
    <>
      {button}
      {menu}
    </>
  )
}

type NavItemMenuContentProps = {
  item: NavItemData
  onClick?: () => void
} & StyleProps

const NavItemMenuMobileListItem = ({
  item,
  onClick,
  className,
  style,
}: NavItemMenuContentProps) => {
  const { t } = useTranslation(['common', 'action', 'generation'])

  const enable = useExperimentParam<boolean>(
    item.experiment ?? '',
    !item.experiment,
  )
  if (!enable) return null

  return (
    <ListItem key={item.title}>
      <ListItemButton
        onClick={onClick}
        className={cx(className, item.className)}
        style={style}
      >
        {t(item.title)}
      </ListItemButton>
    </ListItem>
  )
}

const NavItemMenuDesktopMenuItem = ({
  item,
  onClick,
  className,
  style,
}: NavItemMenuContentProps) => {
  const { t } = useTranslation(['common', 'action', 'generation'])

  const enable = useExperimentParam<boolean>(
    item.experiment ?? '',
    !item.experiment,
  )
  if (!enable) return null

  return (
    <MenuItem
      component={Link}
      to={item.link ?? '#'}
      key={item.title}
      className={cx(className, item.className)}
      onClick={onClick}
      style={style}
    >
      <div className={cx('flex items-center gap-2 w-full h-10 text-base')}>
        {item.icon && <item.icon className="size-5" />}
        <span>{t(item.title)}</span>
        {typeof item.badge === 'string' ? (
          <NavMenuItemBadge type={item.badge} />
        ) : (
          item.badge
        )}
      </div>
    </MenuItem>
  )
}

function NavMenuItemBadge(props: { type: string }) {
  switch (props.type) {
    case 'new':
      return <NewLabel />
    default:
      return null
  }
}

type NavItemCollapseMenuProps = {
  button: ReactNode
  items: NavItemData[]

  isExpanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
}

function NavItemCollapseMenu({
  button,
  items,
  isExpanded,
  onExpandedChange,
}: NavItemCollapseMenuProps) {
  return (
    <Disclosure isExpanded={isExpanded} onExpandedChange={onExpandedChange}>
      {button}

      <DisclosurePanel>
        <ul className="ml-2">
          {items.map(item => (
            <li key={item.title}>
              <NavItem item={item} className="text-wrap" type="list-item" />
            </li>
          ))}
        </ul>
      </DisclosurePanel>
    </Disclosure>
  )
}

export const APP_NAVIGATION_HEIGHT = 60

/*
 * Mobile version
 */

function AppNavigationMobile() {
  const location = useLocation()
  const isActive = (href: string): boolean => location.pathname === href
  const isLooseActive = (href: string): boolean =>
    location.pathname.startsWith(href)
  const { me } = useMe()
  const agentMioUrl = useAgentMioUrl({ utmContent: 'mobile_nav' })

  function renderIconButtonContent() {
    return <FluentAdd24Filled className="size-6" />
  }

  const generateButton = (
    <IconButton
      component={Link}
      to="/generator/image"
      className={cx(
        'bg-purple-600 dark:bg-purple-400 text-white font-semibold text-base',
        'rounded-full',
        'w-16',
      )}
      onClick={() => {
        track('mobile_new_btn_click')
      }}
    >
      {renderIconButtonContent()}
    </IconButton>
  )

  return (
    <div
      className={cx(
        'grid place-items-center text-xs fixed inset-x-0 bottom-0 z-20',
        'bg-gradient-to-t from-background to-background/80 backdrop-blur shadow-[0_-1px_#0002] dark:shadow-[inset_0_1px_#fff2]',
        'grid-cols-[1fr_1fr_1fr_1fr_1fr]',
      )}
      style={{ height: APP_NAVIGATION_HEIGHT }}
    >
      <Link
        to="/"
        data-active={isActive('/') ? '' : null}
        className="group relative flex flex-col items-center w-16 no-underline opacity-60 data-[active]:opacity-100 data-[active]:text-purple-600 dark:data-[active]:text-purple-400"
        onClick={() => track('nav_btn_click', { cate: 'home' })}
      >
        <NavHomeIcon
          className="size-6 text-current"
          strokeWidth={isActive('/') ? 3.5 : 3}
        />
        <span className="font-normal">
          <Text i18nKey="common:home.label" />
        </span>
        <span className="absolute bottom-0 left-1/2 -translate-x-1/2 hidden group-data-[active]:block w-5 h-0.5 rounded-full bg-current" />
      </Link>
      <Link
        to="/market"
        data-active={isActive('/market') ? '' : null}
        className="group relative flex flex-col items-center w-16 no-underline opacity-60 data-[active]:opacity-100 data-[active]:text-purple-600 dark:data-[active]:text-purple-400"
        onClick={() => {
          track('nav_btn_click', { cate: 'market' })
        }}
      >
        <NavMagicWandIcon
          className="size-6 text-current"
          strokeWidth={isActive('/market') ? 3.5 : 3}
        />
        <span className="font-normal">
          <Text i18nKey="common:tab.models" />
        </span>
        <span className="absolute bottom-0 left-1/2 -translate-x-1/2 hidden group-data-[active]:block w-5 h-0.5 rounded-full bg-current" />
      </Link>
      {generateButton}
      <OutboundLink
        href={agentMioUrl}
        className="group flex flex-col items-center no-underline dark:data-[active]:text-white opacity-60 data-[active]:opacity-100 w-16"
        onClick={() => track('nav_btn_click', { cate: 'mio' })}
      >
        <Mio2Logo className="size-6" />
        <span className="font-normal">
          <Text i18nKey="common:tab.mio2" />
        </span>
      </OutboundLink>

      <Link
        to={me ? `/@${me.username}` : '/'}
        data-active={isLooseActive(`/@${me?.username}`) ? '' : null}
        className="group relative flex flex-col items-center no-underline opacity-60 data-[active]:opacity-100 data-[active]:text-purple-600 dark:data-[active]:text-purple-400 w-16"
        onClick={e => {
          track('nav_btn_click', { cate: 'profile' })
          if (!me) {
            e.preventDefault()
            e.stopPropagation()

            openSignModal(null, { fromPath: 'nav_profile' })
          }
        }}
      >
        <NavMineIcon
          className="size-6 text-current"
          strokeWidth={isLooseActive(`/@${me?.username}`) ? 3.5 : 3}
        />
        <span className="font-normal">
          <Text i18nKey="common:tab.profile" />
        </span>
        <span className="absolute bottom-0 left-1/2 -translate-x-1/2 hidden group-data-[active]:block w-5 h-0.5 rounded-full bg-current" />
      </Link>
    </div>
  )
}

/*
 * Export
 */

export default function AppNavigation() {
  const isMobile = useIsMobile()
  return isMobile ? <AppNavigationMobile /> : <AppNavigationDesktop />
}
